import React from 'react'
import moment from 'moment'
import {
  DirectionalHint,
  IColumn,
  Icon,
  IconButton,
  IContextualMenuItem,
} from 'office-ui-fabric-react'
import { Experiment, ExperimentType, isEnded } from '../../Api/experiments'
import { TableView } from '../../Components/TableView'
import { Account } from '../../Api/integrations'

interface Props {
  experiments: Experiment[]
  experimentTypes: ExperimentType[]
  accounts: Account[]
  onDuplicateClick: (experiment: Experiment) => void
  onManageClick: (experiment: Experiment) => void
  onStopClick: (experiment: Experiment) => void
}

export function ExperimentsTable({
  experiments,
  experimentTypes,
  accounts,
  onDuplicateClick,
  onManageClick,
  onStopClick,
}: Props) {
  const columns = useColumns({
    experimentTypes,
    accounts,
    onDuplicateClick,
    onManageClick,
    onStopClick,
  })
  const displayNameById = accounts.reduce<{ [id: string]: string }>(
    (acc, { account, displayName }) => {
      acc[account] = displayName
      return acc
    },
    {}
  )
  const rows = [...experiments].sort(sortExperiments).map((experiment) => {
    const { account, experimentGroup } = experiment.inventory
    const accountIds = account
      ? [account]
      : experimentGroup
        ? experimentGroup.accounts
        : []
    const accountName = accountIds
      .map((id) => displayNameById[id] || '')
      .filter(Boolean)
      .join(' ')
    return { ...experiment, accountName }
  })
  return (
    <TableView
      cacheKey={''}
      columns={columns}
      rowToCacheKey={() => ''}
      rows={rows}
      onRowClick={onManageClick}
    />
  )
}

function useColumns({
  experimentTypes,
  accounts,
  onDuplicateClick,
  onManageClick,
  onStopClick,
}: {
  experimentTypes: ExperimentType[]
  accounts: Account[]
  onDuplicateClick: (experiment: Experiment) => void
  onManageClick: (experiment: Experiment) => void
  onStopClick: (experiment: Experiment) => void
}): IColumn[] {
  const isDeprecated = (experiment: Experiment): boolean => {
    const experimentType = experimentTypes.find(
      ({ type }) => experiment.type === type
    )
    return !!experimentType && experimentType.deprecated
  }
  return [
    {
      key: 'type',
      name: '',
      fieldName: 'type',
      minWidth: 20,
      maxWidth: 20,
      isPadded: false,
      isRowHeader: true,
      isIconOnly: true,
      onRender: (experiment: Experiment) => (
        <Icon
          iconName={
            isEnded(experiment) ? 'TestExploreSolid' : 'TestBeakerSolid'
          }
          style={{
            fontSize: 14,
            paddingLeft: 5,
            paddingTop: 3,
          }}
        />
      ),
    },
    {
      key: 'tag',
      name: 'Experiment Tag',
      fieldName: 'type',
      minWidth: 250,
      maxWidth: 400,
      isPadded: false,
      isRowHeader: true,
      onRender: ({ tag }: Experiment) => <span title={tag}>{tag}</span>,
    },
    {
      key: 'inventory',
      name: 'Inventory',
      fieldName: 'inventory',
      isPadded: true,
      isRowHeader: true,
      minWidth: 0,
      onRender: ({ inventory }: Experiment) => {
        const account = inventory.account
          ? accounts.find(({ account }) => account === inventory.account)
          : null
        const accountName = account ? account.displayName : ''
        const experimentGroupTitle = inventory.experimentGroup
          ? inventory.experimentGroup.title
          : ''
        const display = experimentGroupTitle || inventory.domain || accountName
        return <span title={display}>{display}</span>
      },
    },
    {
      key: 'allocation',
      name: 'Allocation',
      fieldName: 'allocation',
      isPadded: true,
      isRowHeader: true,
      minWidth: 0,
      maxWidth: 75,
      onRender: ({ allocation }: Experiment) => {
        const display = `${allocation}%`
        return <span title={display}>{display}</span>
      },
    },
    {
      key: 'startDate',
      name: 'Start Date',
      fieldName: 'startDate',
      isPadded: true,
      minWidth: 75,
      maxWidth: 100,
      onRender: ({ startDate }: Experiment) => (
        <span>{moment(startDate).format('DD/MM/YYYY')}</span>
      ),
    },
    {
      key: 'endDate',
      name: 'End Date',
      fieldName: 'endDate',
      isPadded: true,
      minWidth: 75,
      maxWidth: 100,
      onRender: ({ endDate }: Experiment) =>
        endDate ? <span>{moment(endDate).format('DD/MM/YYYY')}</span> : null,
    },
    {
      key: 'menu',
      name: '',
      fieldName: '',
      minWidth: 20,
      maxWidth: 20,
      isIconOnly: true,
      onRender: (experiment: Experiment) => (
        <IconButton
          styles={{
            root: {
              height: 18,
            },
            menuIcon: {
              fontWeight: 'bold',
            },
          }}
          menuProps={{
            coverTarget: true,
            directionalHint: DirectionalHint.topCenter,
            items: [
              isDeprecated(experiment)
                ? null
                : {
                    key: 'duplicate',
                    text: 'Duplicate',
                    iconProps: { iconName: 'copy' },
                    onClick: () => onDuplicateClick(experiment),
                  },
              ...(isEnded(experiment)
                ? [
                    {
                      key: 'view',
                      text: 'View',
                      iconProps: { iconName: 'view' },
                      onClick: () => onManageClick(experiment),
                    },
                  ]
                : [
                    {
                      key: 'manage',
                      text: 'Manage',
                      iconProps: { iconName: 'edit' },
                      onClick: () => onManageClick(experiment),
                    },
                    {
                      key: 'stop',
                      text: 'Stop',
                      iconProps: { iconName: 'stop' },
                      onClick: () => onStopClick(experiment),
                    },
                  ]),
            ].filter(Boolean) as IContextualMenuItem[],
          }}
        />
      ),
    },
  ]
}

function sortExperiments(a: Experiment, b: Experiment) {
  return new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
}
